/**
 * Pure, side-effect-free validation for the tier ABM. Mirrors the API's
 * write-invariants (API-CAP-2/3) so the BO fails fast before the round-trip;
 * the API stays authoritative. Unit-tested in `tier-validation.test.ts`.
 */
import {
  MAX_TIER_DAILY_PASS_CAP,
  MAX_TIER_WEIGHT,
  MIN_TIER_DAILY_PASS_CAP,
  MIN_TIER_WEIGHT,
  TIER_DISPLAY_NAME_MAX_LENGTH,
  TIER_KEY_MAX_LENGTH,
  TIER_KEY_PATTERN,
} from "./constants";
import type { SubscriptionTierConfig } from "./types";

export function isTierKeyValid(key: string): boolean {
  const trimmed = key.trim();
  return (
    trimmed.length > 0 && trimmed.length <= TIER_KEY_MAX_LENGTH && TIER_KEY_PATTERN.test(trimmed)
  );
}

export function isDisplayNameValid(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && trimmed.length <= TIER_DISPLAY_NAME_MAX_LENGTH;
}

function isIntInRange(value: number, min: number, max: number): boolean {
  return Number.isInteger(value) && value >= min && value <= max;
}

export function isWeightValid(weight: number): boolean {
  return isIntInRange(weight, MIN_TIER_WEIGHT, MAX_TIER_WEIGHT);
}

export function isDailyPassCapValid(cap: number): boolean {
  return isIntInRange(cap, MIN_TIER_DAILY_PASS_CAP, MAX_TIER_DAILY_PASS_CAP);
}

/**
 * True when `weight` is free among the existing tiers. `excludeId` skips the row
 * being edited so a tier keeping its own weight isn't reported as a clash.
 */
export function isWeightUnique(
  weight: number,
  tiers: readonly SubscriptionTierConfig[],
  excludeId?: string,
): boolean {
  return !tiers.some((t) => t.id !== excludeId && t.weight === weight);
}

/** True when `key` is free among existing tiers (case-sensitive, like the API). */
export function isKeyUnique(
  key: string,
  tiers: readonly SubscriptionTierConfig[],
  excludeId?: string,
): boolean {
  const trimmed = key.trim();
  return !tiers.some((t) => t.id !== excludeId && t.key === trimmed);
}

/**
 * True when linking `stripeProductId` would not collide with another tier
 * (one Product ↔ at most one tier). A null/empty link never collides.
 */
export function isProductLinkUnique(
  stripeProductId: string | null,
  tiers: readonly SubscriptionTierConfig[],
  excludeId?: string,
): boolean {
  if (!stripeProductId) return true;
  return !tiers.some((t) => t.id !== excludeId && t.stripeProductId === stripeProductId);
}

/**
 * Whether deactivating `tier` is allowed: there must always remain ≥ 1 active
 * tier. Returns false only when `tier` is the sole active tier (API-CAP-3
 * `LAST_ACTIVE_TIER`). Deactivating an already-inactive tier is a no-op (true).
 */
export function canDeactivate(
  tier: SubscriptionTierConfig,
  tiers: readonly SubscriptionTierConfig[],
): boolean {
  if (!tier.active) return true;
  return tiers.some((t) => t.id !== tier.id && t.active);
}
